import api from "./api.service";

/* ─── Tipos (respuestas de /api/oece, ver /srv/oece-api/app.py) ─── */
export interface TipoRnp {
  codigo: string;
  nombre: string;
}

export interface Antecedentes {
  sanciones: number;
  inhab_judicial: number;
  inhab_administrativa: number;
  medidas_cautelares: number;
  penalidades: number;
  alguna_vigente: boolean;
}

export interface ProveedorResumen {
  ruc: string;
  razon_social: string | null;
  rnp: { registrado: boolean; vigente: boolean; tipos: TipoRnp[]; tipos_vigentes: TipoRnp[] };
  habilitado: boolean | null;
  apto_contratar: boolean | null;
  antecedentes: Antecedentes;
  consultado: string;
  ultimo_cambio: string;
}

/* Datos de la resolución del Tribunal publicada en gob.pe */
export interface ResolucionInfo {
  expediente: string | null;
  tipo: "sancion" | "reconsideracion" | "otro" | null;
  recurrida: string | null;
  resultado: string | null;
  url: string | null;
  url_pagina: string | null;
}

export interface Sancion {
  nroRes?: string;
  descripcion?: string;
  periodo?: string;
  vigente?: boolean;
  fechaIni?: string;
  fechaFin?: string;
  fechaResol?: string;
  motivos?: string[];
  montoTexto?: string;
  indPago?: string;
  textoGlosa?: string;
  tipo?: string;
  resolucion?: ResolucionInfo | null;
  [k: string]: unknown;
}

export interface Penalidad {
  fecha?: string;
  montoTexto?: string;
  causal?: string;
  descripcion?: string;
  nomEntCont?: string;
  desContProv?: string;
  desCatObj2?: string;
  [k: string]: unknown;
}

export interface ProveedorDetalle extends ProveedorResumen {
  contacto: { emails: string[]; telefonos: string[] };
  detalle_antecedentes: {
    sanciones: Sancion[];
    inhab_judicial: Sancion[];
    inhab_administrativa: Sancion[];
    medidas_cautelares: Sancion[];
    penalidades: Penalidad[];
  };
  primera_vez?: string;
  vigentes?: Sancion[];
}

export interface Pagina<T> {
  total: number;
  page: number;
  size: number;
  items: T[];
}

export interface Corrida {
  id: number;
  stamp: string;
  inicio: string;
  fin: string | null;
  total: number | null;
  existen: number | null;
  vigentes: number | null;
  fallidos: number | null;
}

export interface Stats {
  proveedores: number;
  sin_ficha_con_antecedentes: number;
  rnp_registrados: number;
  rnp_vigentes: number;
  habilitados: number;
  no_habilitados: number;
  sancionados_vigentes: number;
  con_sanciones: number;
  con_penalidades: number;
  rucs_consultados: number;
  ultima_consulta: string;
  rnp_vigentes_por_tipo: { codigo: string; nombre: string; proveedores: number }[];
  ultima_corrida: Corrida | null;
}

export interface Oportunidad {
  etapa: "reconsideracion" | "contencioso";
  ruc: string;
  razon_social: string;
  resolucion: string;
  fecha_resolucion: string;
  dias_habiles_desde_resolucion: number;
  dias_para_limite_contencioso: number;
  tipo_sancion: string;
  periodo: string;
  inicio: string;
  fin: string;
  motivos: string[];
  monto_multa: string | null;
  habilitado: boolean;
  rnp_tipos_vigentes: TipoRnp[];
  otras_sanciones: number;
  penalidades: number;
  emails: string[];
  telefonos: string[];
  expediente: string | null;
  url_resolucion: string | null;
  reconsideracion_resuelta: boolean;
  resultado_reconsideracion: string | null;
  resolucion_sancion: string | null;
}

export interface Oportunidades {
  hoy: string;
  plazos: { reconsideracion_dias_habiles: number; contencioso_meses: number };
  total: number;
  por_etapa: { reconsideracion: number; contencioso: number };
  items: Oportunidad[];
}

export interface Cambio {
  ruc: string;
  razon_social: string | null;
  fecha: string;
  cambios: Record<string, { antes: unknown; ahora: unknown }>;
}

export interface Cambios extends Pagina<Cambio> {
  desde: string;
}

export interface FiltrosProveedores {
  q?: string;
  tipo?: string;
  rnp_vigente?: boolean;
  habilitado?: boolean;
  sancionado?: boolean;
  con_sanciones?: boolean;
  con_penalidades?: boolean;
  con_contacto?: boolean;
  orden?: string;
  page?: number;
  size?: number;
}

/* ─── Llamadas ─── */
export const getOeceStats = () => api.get<Stats>("/oece/stats");

export const buscarProveedores = (params: FiltrosProveedores) =>
  api.get<Pagina<ProveedorResumen>>("/oece/proveedores", { params });

export const getFichaProveedor = (ruc: string) =>
  api.get<ProveedorDetalle>(`/oece/proveedores/${ruc}`);

export const getHistorialProveedor = (ruc: string) =>
  api.get<{ ruc: string; versiones: (ProveedorDetalle & { valido_hasta: string })[] }>(
    `/oece/proveedores/${ruc}/historial`
  );

export const getSancionados = (page: number, size: number) =>
  api.get<Pagina<ProveedorDetalle>>("/oece/sancionados", { params: { page, size } });

export const getCambios = (params: { desde?: string; campo?: string; page?: number; size?: number }) =>
  api.get<Cambios>("/oece/cambios", { params });

export const getOportunidades = (params: { etapa?: string; incluir_multas?: boolean }) =>
  api.get<Oportunidades>("/oece/oportunidades", { params });

export interface Cautelar {
  id?: number;
  ruc: string;
  razon_social: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  vigente: boolean;
  anio: number | null;
  habilitado: boolean | null;
  sancion_vigente: boolean | null;
  descripcion: string;
  organo_clave: string | null;
  expediente_judicial: string | null;
  via: string;
  instancia: "Juzgado" | "Sala Superior" | null;
  organo: string | null;
  juez: string | null;
  provisional: boolean;
  resoluciones_sancion: string[];
}

interface Conteo<T> {
  valor: T;
  n: number;
}

export interface Cautelares {
  total: number;
  empresas: number;
  por_anio: Conteo<number>[];
  por_via: Conteo<string>[];
  por_instancia: Conteo<string>[];
  por_organo: Conteo<string>[];
  nota: string;
  items: Cautelar[];
}

export const getCautelares = () => api.get<Cautelares>("/oece/cautelares");

export interface Candidato {
  ruc: string;
  razon_social: string | null;
  puntaje: number;
  prioridad: "alta" | "media" | "baja";
  motivos_puntaje: { puntos: number; motivo: string }[];
  en_plazo_contencioso: boolean;
  limite_contencioso: string | null;
  dias_para_limite: number | null;
  definitiva: boolean;
  meses_restantes: number | null;
  fin_sancion: string | null;
  resolucion: string | null;
  fecha_resolucion: string | null;
  tipo_sancion: string | null;
  periodo: string | null;
  motivos: string[];
  expediente: string | null;
  url_resolucion: string | null;
  reconsideracion_resuelta: boolean;
  infracciones: string[];
  precedentes: number;
  organos_precedentes: { organo: string; n: number }[];
  obras: boolean;
  rnp_tipos_vigentes: TipoRnp[];
  otras_sanciones: number;
  penalidades: number;
  emails: string[];
  telefonos: string[];
}

export interface Candidatos {
  hoy: string;
  total: number;
  por_prioridad: { alta: number; media: number; baja: number };
  en_plazo: number;
  obras: number;
  precedentes_por_infraccion: { infraccion: string; n: number }[];
  items: Candidato[];
}

export const getCandidatosCautelar = () => api.get<Candidatos>("/oece/candidatos-cautelar");

/* Descarga un CSV autenticado (el token va por header, no sirve un <a href>) */
async function descargar(url: string, params: object, nombre: string) {
  const resp = await api.get(url, { params, responseType: "blob" });
  const href = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(href);
}

export const exportarProveedores = (params: FiltrosProveedores) =>
  descargar("/oece/export/proveedores.csv", params, "proveedores-oece.csv");

export const exportarOportunidades = (params: { etapa?: string; incluir_multas?: boolean }) =>
  descargar("/oece/export/oportunidades.csv", params, "oportunidades-oece.csv");

export const exportarCandidatos = () =>
  descargar("/oece/export/candidatos-cautelar.csv", {}, "candidatos-cautelar.csv");
